import { LegalPageLayout, Section, Bullet } from "@/components/legal/LegalPageLayout";

const LAST_UPDATED = "18 March 2026";

export function TermsOfService() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      lastUpdated={LAST_UPDATED}
      crossLink={{ to: "/privacy", label: "Privacy Policy" }}
      contactEmail="legal@chargesuite.io"
    >
      <p className="mt-6 text-sm leading-relaxed text-[var(--color-text-secondary)]">
        These Terms of Service (&ldquo;Terms&rdquo;) govern access to and
        use of the ChargeSuite platform (&ldquo;Service&rdquo;) provided by
        ChargeSuite GmbH (&ldquo;ChargeSuite&rdquo;, &ldquo;we&rdquo;, or
        &ldquo;us&rdquo;). By creating an account or using the Service, the
        legal entity you represent (&ldquo;Customer&rdquo; or
        &ldquo;you&rdquo;) agrees to these Terms. These Terms constitute a
        binding agreement between ChargeSuite and Customer. If you do not
        agree, do not use the Service.
      </p>

      {/* 1. Service Description */}
      <Section id="section-service" title="1. Service Description">
        <p>
          ChargeSuite is an AI-powered regulatory compliance and grid
          revenue optimisation platform for EV charging operators
          (&ldquo;CPOs&rdquo;) operating in Germany and the European Union.
          The Service includes:
        </p>
        <ul className="space-y-2">
          {[
            "Compliance Brain: RAG-based AI system trained on German and EU EV charging regulations (AFIR, Eichrecht, EnWG, LSV, BDEW, CRA, ISO 15118) that answers compliance questions with cited regulatory sources",
            "Gap Analysis: Automated assessment of the Customer's regulatory gaps with prioritised action plans and cost estimates",
            "Regulatory Alerts: Deadline monitoring and change notifications for applicable regulations",
            "V2G Optimisation (GridBrain): Revenue optimisation for Vehicle-to-Grid events, subject to compliance validation",
            "Subsidy Matching: Identification and eligibility assessment of applicable EV infrastructure subsidies",
            "CPMS Integration: Read-only connectivity to the Customer's existing Charge Point Management System via supported APIs",
            "Reporting: Automated DATEX II and intelligence reports",
          ].map((item) => (
            <Bullet key={item}>{item}</Bullet>
          ))}
        </ul>
        <p>
          The Service does not manage chargers directly, communicate via
          OCPP, or process end-customer payments. All AI-generated outputs
          are advisory only and do not constitute legal advice. Customers
          must independently verify compliance requirements with qualified
          legal counsel.
        </p>
      </Section>

      {/* 2. Subscription Tiers */}
      <Section id="section-tiers" title="2. Subscription Tiers">
        <p>
          The Service is offered on the following subscription tiers.
          Feature availability and usage limits are detailed on the Pricing
          page:
        </p>
        <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-gray-50">
                <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
                  Tier
                </th>
                <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
                  Price (EUR/month)
                </th>
                <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
                  Target Customer
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {[
                {
                  tier: "Free Trial",
                  price: "0",
                  target:
                    "Early-stage operators evaluating compliance posture",
                },
                {
                  tier: "Professional",
                  price: "299",
                  target:
                    "Growing operators requiring full compliance tooling and V2G optimisation",
                },
                {
                  tier: "Enterprise",
                  price: "Custom",
                  target:
                    "Large-scale operators requiring custom integrations, SLA guarantees, and dedicated support",
                },
              ].map((row) => (
                <tr key={row.tier} className="align-top">
                  <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">
                    {row.tier}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {row.price}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {row.target}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          ChargeSuite reserves the right to modify pricing with 60 days
          written notice to existing subscribers. Price changes do not
          apply to prepaid annual subscriptions until renewal.
        </p>
      </Section>

      {/* 3. Payment Terms */}
      <Section id="section-payment" title="3. Payment Terms">
        <p>
          Subscriptions are billed via Stripe in advance on a monthly or
          annual basis. The following payment terms apply:
        </p>
        <ul className="space-y-2">
          {[
            "Monthly subscriptions are billed on the same calendar day each month. Annual subscriptions are billed at the start of each annual period at a discounted rate (where applicable).",
            "All fees are exclusive of applicable taxes (VAT/USt). Customers in Germany will be charged VAT at the applicable rate unless a valid reverse-charge mechanism applies.",
            "Payment is due immediately upon invoice issuance. Overdue amounts accrue interest at 9 percentage points above the ECB base rate per annum under German law (BGB § 288).",
            "In the event of failed payment, ChargeSuite will attempt to collect for 7 days before suspending the account. Service resumes upon receipt of payment.",
            "Refunds are not available for partial months except as required by applicable law.",
          ].map((item) => (
            <Bullet key={item}>{item}</Bullet>
          ))}
        </ul>
      </Section>

      {/* 4. Service Level Agreement */}
      <Section id="section-sla" title="4. Service Level Agreement">
        <p>
          ChargeSuite commits to the following service levels for paid
          Professional and Enterprise tier customers:
        </p>
        <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-gray-50">
                <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
                  Metric
                </th>
                <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
                  Commitment
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {[
                {
                  metric: "Monthly uptime",
                  commitment: "99.5% (excludes scheduled maintenance)",
                },
                {
                  metric: "Scheduled maintenance window",
                  commitment:
                    "Sundays 02:00–06:00 CET; 48 hours advance notice",
                },
                {
                  metric: "Critical incident response",
                  commitment: "2 hours (service outage or data loss risk)",
                },
                {
                  metric: "Standard support response",
                  commitment: "1 business day (Professional); 4 hours (Enterprise)",
                },
              ].map((row) => (
                <tr key={row.metric} className="align-top">
                  <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">
                    {row.metric}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {row.commitment}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          If monthly uptime falls below 99.5%, affected Customers are
          entitled to a service credit equal to 5% of the monthly
          subscription fee for each full percentage point below the SLA
          threshold, up to a maximum of 30% of the monthly fee. Credits
          must be claimed within 30 days and will be applied to the
          following invoice. SLA credits are the Customer&apos;s sole
          remedy for downtime.
        </p>
      </Section>

      {/* 5. Intellectual Property */}
      <Section id="section-ip" title="5. Intellectual Property">
        <p>
          <strong className="font-medium text-[var(--color-text-primary)]">
            Customer data:
          </strong>{" "}
          All data uploaded or generated by the Customer (operator profiles,
          charger inventories, compliance Q&A content, session metadata)
          remains the exclusive property of the Customer. ChargeSuite
          processes such data solely to provide the Service and as described
          in our Privacy Policy.
        </p>
        <p>
          <strong className="font-medium text-[var(--color-text-primary)]">
            Platform IP:
          </strong>{" "}
          ChargeSuite retains all rights, title, and interest in the
          Service, including the Compliance Brain AI system, RAG pipeline,
          GridBrain optimisation algorithms, regulatory corpus, and all
          associated software, documentation, and trademarks. Nothing in
          these Terms grants the Customer any rights in the platform beyond
          the limited licence to use the Service set out herein.
        </p>
        <p>
          <strong className="font-medium text-[var(--color-text-primary)]">
            Feedback:
          </strong>{" "}
          Any feedback, suggestions, or improvement ideas provided by the
          Customer may be used by ChargeSuite without restriction or
          compensation.
        </p>
      </Section>

      {/* 6. Acceptable Use */}
      <Section id="section-use" title="6. Acceptable Use">
        <p>Customers must not:</p>
        <ul className="space-y-2">
          {[
            "Use the Service for any unlawful purpose or in violation of applicable regulations",
            "Attempt to reverse-engineer, decompile, or extract the underlying AI models or regulatory corpus",
            "Resell, sublicense, or make the Service available to third parties without written consent",
            "Transmit malicious code, launch denial-of-service attacks, or attempt to gain unauthorised access",
            "Use the Service to process data relating to individuals without a valid lawful basis under GDPR",
            "Represent AI-generated compliance outputs as formal legal advice to third parties",
          ].map((item) => (
            <Bullet key={item}>{item}</Bullet>
          ))}
        </ul>
      </Section>

      {/* 7. Liability */}
      <Section id="section-liability" title="7. Limitation of Liability">
        <p>
          To the maximum extent permitted by applicable law:
        </p>
        <ul className="space-y-3">
          {[
            "ChargeSuite's total aggregate liability for any claims arising under or related to these Terms in any 12-month period is limited to the fees actually paid by the Customer in that period.",
            "ChargeSuite is not liable for indirect, incidental, special, consequential, or punitive damages, including lost profits, loss of data, or business interruption, even if advised of the possibility of such damages.",
            "ChargeSuite makes no warranty that the Service will be error-free, that AI-generated compliance outputs are legally complete or accurate, or that use of the Service will ensure regulatory compliance. All outputs are advisory.",
            "Nothing in these Terms limits liability for (i) death or personal injury caused by negligence, (ii) fraud or fraudulent misrepresentation, or (iii) any other liability that cannot be excluded under German law.",
          ].map((item) => (
            <Bullet key={item}>{item}</Bullet>
          ))}
        </ul>
      </Section>

      {/* 8. Termination */}
      <Section id="section-termination" title="8. Termination">
        <p>
          <strong className="font-medium text-[var(--color-text-primary)]">
            By the Customer:
          </strong>{" "}
          Monthly subscribers may cancel at any time with effect from the
          end of the current billing period. Annual subscribers may
          terminate with 30 days written notice; refunds are proportional
          to unused months.
        </p>
        <p>
          <strong className="font-medium text-[var(--color-text-primary)]">
            By ChargeSuite:
          </strong>{" "}
          ChargeSuite may terminate or suspend the Service with 30 days
          written notice for any reason, or immediately in the event of
          material breach of these Terms, insolvency, or activity that
          poses a security or legal risk.
        </p>
        <p>
          <strong className="font-medium text-[var(--color-text-primary)]">
            Data export:
          </strong>{" "}
          Upon termination, the Customer has 30 days to export their data
          via the dashboard export function or by requesting a data export
          at{" "}
          <a
            href="mailto:support@chargesuite.io"
            className="text-[var(--color-primary)] hover:underline"
          >
            support@chargesuite.io
          </a>
          . After 30 days, data will be deleted in accordance with our
          Privacy Policy.
        </p>
      </Section>

      {/* 9. Confidentiality */}
      <Section id="section-confidentiality" title="9. Confidentiality">
        <p>
          Each party agrees to keep confidential the other party&apos;s
          non-public business information disclosed in connection with the
          Service (&ldquo;Confidential Information&rdquo;). Confidential
          Information does not include information that is (a) publicly
          known, (b) independently developed without reference to the
          disclosing party&apos;s information, or (c) required to be
          disclosed by law.
        </p>
      </Section>

      {/* 10. Changes */}
      <Section id="section-changes" title="10. Changes to These Terms">
        <p>
          ChargeSuite may modify these Terms with 30 days written notice
          to registered Customer contacts. Continued use of the Service
          after the notice period constitutes acceptance of the revised
          Terms. For material changes, Customers who do not accept the
          revised Terms may terminate their subscription within the notice
          period without penalty.
        </p>
      </Section>

      {/* 11. Governing Law */}
      <Section id="section-law" title="11. Governing Law and Jurisdiction">
        <p>
          These Terms are governed by the laws of the Federal Republic of
          Germany, excluding conflict-of-law rules and the UN Convention on
          Contracts for the International Sale of Goods (CISG).
        </p>
        <p>
          Any disputes arising out of or in connection with these Terms
          shall be subject to the exclusive jurisdiction of the courts of
          Frankfurt am Main, Germany. Mandatory statutory provisions
          regarding jurisdiction of the Customer&apos;s place of
          establishment remain unaffected.
        </p>
      </Section>

      {/* 12. Miscellaneous */}
      <Section id="section-misc" title="12. Miscellaneous">
        <ul className="space-y-2">
          {[
            "Entire agreement: These Terms, together with the Data Processing Agreement and any executed Order Form, constitute the entire agreement between the parties and supersede all prior representations.",
            "Severability: If any provision is found unenforceable, the remaining provisions remain in full force.",
            "Waiver: Failure to enforce any right does not constitute a waiver of that right.",
            "Assignment: ChargeSuite may assign these Terms in connection with a merger, acquisition, or sale of assets. Customer may not assign without prior written consent.",
            "Notices: Legal notices must be sent to legal@chargesuite.io and to the Customer's registered contact email.",
          ].map((item) => (
            <Bullet key={item}>{item}</Bullet>
          ))}
        </ul>
      </Section>
    </LegalPageLayout>
  );
}
